import { zodResolver } from "@hookform/resolvers/zod";
import { Box, Tab, Tabs } from "@mui/material";
import { type ChangeEvent, useEffect, useState } from "react";
import { Controller, useForm, useWatch } from "react-hook-form";
import { z } from "zod";
import { RequiredLabel } from "../../../components/form/RequiredLabel";
import { Input } from "../../../components/input";
import { ImageUploadCropper } from "../../../components/image-upload-cropper";
import { SelectV2 } from "../../../components/select-v2";
import { organizationTypeLabels } from "../constants/organization.constants";
import { organizationTypes, type Organization, type OrganizationPayload, type OrganizationType } from "../types/organization.types";

const mapConfigFieldNames = [
  "mapCenterLatitude",
  "mapCenterLongitude",
  "mapMaxBoundsSouthLatitude",
  "mapMaxBoundsWestLongitude",
  "mapMaxBoundsNorthLatitude",
  "mapMaxBoundsEastLongitude",
  "mapZoom",
] as const;

type MapConfigFieldName = (typeof mapConfigFieldNames)[number];

const organizationEditorSchema = z
  .object({
    name: z.string().trim().min(1, "Organization name is required.").max(255),
    organizationType: z.enum(organizationTypes).nullable(),
    state: z.string().trim().min(1, "State is required.").max(255),
    image: z.instanceof(File).nullable(),
    boundaryGeoJson: z.instanceof(File).nullable(),
    mapCenterLatitude: z.number().finite().nullable(),
    mapCenterLongitude: z.number().finite().nullable(),
    mapMaxBoundsSouthLatitude: z.number().finite().nullable(),
    mapMaxBoundsWestLongitude: z.number().finite().nullable(),
    mapMaxBoundsNorthLatitude: z.number().finite().nullable(),
    mapMaxBoundsEastLongitude: z.number().finite().nullable(),
    mapZoom: z.number().int().nullable(),
  })
  .superRefine((values, context) => {
    if (values.organizationType !== "city") return;

    const mapValues = mapConfigFieldNames.map((field) => values[field]);
    const hasMapConfig = mapValues.some((value) => value !== null);
    if (!hasMapConfig) return;

    mapConfigFieldNames.forEach((field) => {
      if (values[field] === null) {
        context.addIssue({ code: z.ZodIssueCode.custom, path: [field], message: "Required with map configuration." });
      }
    });

    if (mapValues.some((value) => value === null)) return;

    const {
      mapCenterLatitude,
      mapCenterLongitude,
      mapMaxBoundsSouthLatitude,
      mapMaxBoundsWestLongitude,
      mapMaxBoundsNorthLatitude,
      mapMaxBoundsEastLongitude,
      mapZoom,
    } = values;

    if (
      Math.abs(mapCenterLatitude!) > 90 ||
      Math.abs(mapMaxBoundsSouthLatitude!) > 90 ||
      Math.abs(mapMaxBoundsNorthLatitude!) > 90 ||
      Math.abs(mapCenterLongitude!) > 180 ||
      Math.abs(mapMaxBoundsWestLongitude!) > 180 ||
      Math.abs(mapMaxBoundsEastLongitude!) > 180
    ) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["mapCenterLatitude"], message: "Map coordinates are out of range." });
    }

    if (mapMaxBoundsSouthLatitude! > mapMaxBoundsNorthLatitude! || mapMaxBoundsWestLongitude! > mapMaxBoundsEastLongitude!) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["mapMaxBoundsSouthLatitude"], message: "Map max bounds are invalid." });
    }

    if (mapZoom! < 1 || mapZoom! > 22) {
      context.addIssue({ code: z.ZodIssueCode.custom, path: ["mapZoom"], message: "Map zoom must be between 1 and 22." });
    }
  });

type OrganizationEditorValues = z.input<typeof organizationEditorSchema>;
type OrganizationEditorOutput = z.output<typeof organizationEditorSchema>;

const organizationTypeOptions = organizationTypes.map((type) => ({
  value: type,
  label: organizationTypeLabels[type],
}));

const mapInputDefinitions: ReadonlyArray<{
  name: MapConfigFieldName;
  label: string;
  min: number;
  max: number;
  step: number;
}> = [
  { name: "mapCenterLatitude", label: "Center latitude", min: -90, max: 90, step: 0.000001 },
  { name: "mapCenterLongitude", label: "Center longitude", min: -180, max: 180, step: 0.000001 },
  { name: "mapMaxBoundsSouthLatitude", label: "South latitude", min: -90, max: 90, step: 0.000001 },
  { name: "mapMaxBoundsWestLongitude", label: "West longitude", min: -180, max: 180, step: 0.000001 },
  { name: "mapMaxBoundsNorthLatitude", label: "North latitude", min: -90, max: 90, step: 0.000001 },
  { name: "mapMaxBoundsEastLongitude", label: "East longitude", min: -180, max: 180, step: 0.000001 },
  { name: "mapZoom", label: "Zoom", min: 1, max: 22, step: 1 },
];

type OrganizationEditorProps = {
  formId: string;
  organization: Organization | null;
  isOpen: boolean;
  saving: boolean;
  error: string | null;
  onSubmit: (payload: OrganizationPayload) => Promise<void>;
};

const toOptionalNumber = (value: string) => (value === "" ? null : Number(value));

export function OrganizationEditor({ formId, organization, isOpen, saving, error, onSubmit }: OrganizationEditorProps) {
  const { control, register, handleSubmit, reset, formState: { errors } } = useForm<OrganizationEditorValues, undefined, OrganizationEditorOutput>({
    resolver: zodResolver(organizationEditorSchema),
    defaultValues: {
      name: "", organizationType: null, state: "", image: null, boundaryGeoJson: null,
      mapCenterLatitude: null, mapCenterLongitude: null, mapMaxBoundsSouthLatitude: null,
      mapMaxBoundsWestLongitude: null, mapMaxBoundsNorthLatitude: null, mapMaxBoundsEastLongitude: null, mapZoom: null,
    },
  });
  const organizationType = useWatch({ control, name: "organizationType" });
  const [activeTab, setActiveTab] = useState(0);

  useEffect(() => {
    if (!isOpen) return;

    setActiveTab(0);
    const mapConfig = organization?.map_config;
    reset({
      name: organization?.name ?? "",
      organizationType: organization?.organization_type ?? null,
      state: organization?.state ?? "",
      image: null,
      boundaryGeoJson: null,
      mapCenterLatitude: mapConfig?.center[0] ?? null,
      mapCenterLongitude: mapConfig?.center[1] ?? null,
      mapMaxBoundsSouthLatitude: mapConfig?.max_bounds[0][0] ?? null,
      mapMaxBoundsWestLongitude: mapConfig?.max_bounds[0][1] ?? null,
      mapMaxBoundsNorthLatitude: mapConfig?.max_bounds[1][0] ?? null,
      mapMaxBoundsEastLongitude: mapConfig?.max_bounds[1][1] ?? null,
      mapZoom: mapConfig?.zoom ?? null,
    });
  }, [isOpen, organization, reset]);

  return (
    <form id={formId} onSubmit={(event) => void handleSubmit((values) => {
      const hasMapConfig = mapConfigFieldNames.some((field) => values[field] !== null);
      return onSubmit({
        name: values.name.trim(), organization_type: values.organizationType, state: values.state.trim(), image: values.image,
        boundary_geojson: values.boundaryGeoJson,
        ...(values.organizationType === "city" && hasMapConfig ? {
          map_center_latitude: values.mapCenterLatitude!, map_center_longitude: values.mapCenterLongitude!,
          map_max_bounds_south_latitude: values.mapMaxBoundsSouthLatitude!, map_max_bounds_west_longitude: values.mapMaxBoundsWestLongitude!,
          map_max_bounds_north_latitude: values.mapMaxBoundsNorthLatitude!, map_max_bounds_east_longitude: values.mapMaxBoundsEastLongitude!, map_zoom: values.mapZoom!,
        } : {}),
      });
    })(event)} className="space-y-4">
      <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
        <Tabs value={activeTab} onChange={(_, value: number) => setActiveTab(value)} aria-label="Organization editor sections">
          <Tab label="Basic configuration" />
          <Tab label="Map configuration" disabled={organizationType !== "city"} />
        </Tabs>
      </Box>
      {activeTab === 0 ? <div className="space-y-6">
      <Controller control={control} name="image" render={({ field }) => (
        <ImageUploadCropper value={field.value ?? organization?.icon_url ?? null} onChange={field.onChange} aspect={1} cropShape="round" previewShape="round" title="Organization logo" description="Upload and crop a square logo." outputFileName="organization_logo.jpg" disabled={saving} error={errors.image?.message} />
      )} />
      <div className="flex flex-col gap-1 text-sm font-medium">
        <RequiredLabel required htmlFor="organization-name">Name</RequiredLabel>
        <Input id="organization-name" required {...register("name")} disabled={saving} />
        {errors.name ? <span className="text-sm text-red-500">{errors.name.message}</span> : null}
      </div>
      <label className="block text-sm font-medium">
        Type
        <Controller control={control} name="organizationType" render={({ field }) => (
          <SelectV2 value={field.value ?? ""} onChange={(value) => field.onChange((value || null) as OrganizationType | null)} options={organizationTypeOptions} placeholder="Select type" ariaLabel="Organization type" disabled={saving} />
        )} />
      </label>
      <div className="flex flex-col gap-1 text-sm font-medium">
        <RequiredLabel required htmlFor="organization-state">State</RequiredLabel>
        <Input id="organization-state" required {...register("state")} disabled={saving} />
        {errors.state ? <span className="text-sm text-red-500">{errors.state.message}</span> : null}
      </div>
      </div> : null}
      {activeTab === 1 && organizationType === "city" ? (
        <section className="space-y-4">
          <div>
            <h3 className="text-sm font-semibold text-slate-900">City map configuration</h3>
            <p className="mt-1 text-sm text-slate-500">Upload an optional GeoJSON boundary. If you enter map values, complete all seven fields.</p>
          </div>
          <div className="flex flex-col gap-1 text-sm font-medium">
            <label htmlFor="organization-boundary-geojson">Boundary GeoJSON</label>
            <Controller control={control} name="boundaryGeoJson" render={({ field }) => (
              <Input
                id="organization-boundary-geojson"
                type="file"
                accept=".geojson,.json,application/geo+json,application/json"
                disabled={saving}
                onChange={(event: ChangeEvent<HTMLInputElement>) =>
                  field.onChange(event.currentTarget.files?.[0] ?? null)
                }
              />
            )} />
            {organization?.boundary_geojson_url ? <a href={organization.boundary_geojson_url} target="_blank" rel="noreferrer" className="text-sm text-emerald-700 underline">View current boundary</a> : null}
            {errors.boundaryGeoJson ? <span className="text-sm text-red-500">{errors.boundaryGeoJson.message}</span> : null}
          </div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            {mapInputDefinitions.map((mapField) => (
              <Controller key={mapField.name} control={control} name={mapField.name} render={({ field }) => (
                <label className="flex flex-col gap-1 text-sm font-medium">
                  {mapField.label}
                  <Input type="number" min={mapField.min} max={mapField.max} step={mapField.step} inputMode="decimal" value={field.value ?? ""} onChange={(event) => field.onChange(toOptionalNumber(event.target.value))} disabled={saving} />
                  {errors[mapField.name] ? <span className="text-sm text-red-500">{errors[mapField.name]?.message}</span> : null}
                </label>
              )} />
            ))}
          </div>
        </section>
      ) : null}
      {error ? <p className="text-sm text-[#EC5454]">{error}</p> : null}
    </form>
  );
}
